/* ============================================================================
   CREATOR PROMO LANDING PAGE — runtime
   Powers sections/creator-promo-*.liquid. Ships no third-party JS.

   Three jobs, and nothing else. There is no data fetching and no state beyond
   which FAQ row is open and which video is playing:
     1. Video: nothing ever autoplays. Every clip waits for the visitor to
        press play, starts with sound, only one plays at a time, and anything
        that scrolls out of view pauses.
     2. Social embeds: the iframe is injected on first interaction, never
        eagerly — embed scripts are the biggest LCP/CLS risk on this page.
        Their thumbnails come from the platform: YouTube's is a static URL set
        server-side and only needs a fallback chain here, while TikTok and
        Vimeo need an oEmbed lookup, done lazily as the card nears the view.
     3. FAQ: single-open accordion over native <details>.

   Everything is initialised per section id and re-initialised on
   shopify:section:load so the theme editor stays live.
   ========================================================================== */
(function () {
  'use strict';

  /* Each creator-promo-* section renders snippets/creator-promo-assets.liquid,
     so a page using all nine parses nine identical <script> tags. Duplicate
     script tags are fetched once but EXECUTED once each, which would give each
     run its own `players` registry and its own IntersectionObserver — and the
     "only one video plays at a time" guarantee only holds within one registry.
     Bail on every run after the first. */
  if (window.__creatorPromoRuntime) return;
  window.__creatorPromoRuntime = true;

  /* Every player on the page, so starting one can stop the rest. */
  var players = [];

  function pauseOthers(current) {
    players.forEach(function (player) {
      if (player !== current) player.pause();
    });
  }

  /* --- Player ------------------------------------------------------------
     Wraps one .cp-media. Every player is press-to-play; there is no autoplay
     mode, by product decision — autoplaying video on a landing page is a poor
     experience and the visitor arrived from a link, not to be shouted at. */
  function Player(root) {
    this.root = root;
    this.video = root.querySelector('video');
    this.playBtn = root.querySelector('[data-cp-play]');
    this.soundBtn = root.querySelector('[data-cp-sound]');
    this.embed = root.dataset.cpEmbed || '';
    this.started = false;

    this.bind();
  }

  Player.prototype.bind = function () {
    var self = this;

    if (this.playBtn) {
      this.playBtn.addEventListener('click', function () {
        self.start();
      });
    }

    /* Tapping the video itself toggles playback, matching the reel players
       elsewhere in the theme. */
    if (this.video) {
      this.video.addEventListener('click', function () {
        if (self.video.paused) self.start();
        else self.pause();
      });

      this.video.addEventListener('play', function () {
        self.root.classList.add('is-playing');
        pauseOthers(self);
      });
      this.video.addEventListener('pause', function () {
        self.root.classList.remove('is-playing');
      });

      /* A finished clip fires `ended`, not `pause`, in Chrome — without this
         the poster would stay hidden and the play button would stay gone,
         leaving a dead last frame with no way to replay. */
      this.video.addEventListener('ended', function () {
        self.root.classList.remove('is-playing');
        self.video.currentTime = 0;
      });
    }

    if (this.soundBtn) {
      this.soundBtn.addEventListener('click', function () {
        self.toggleSound();
      });
      this.syncSoundLabel();
    }
  };

  /* Injects the social embed on first interaction. Returns true when this
     player is embed-backed, so start() knows not to look for a <video>. */
  Player.prototype.mountEmbed = function () {
    if (!this.embed || this.started) return !!this.embed;

    var frame = document.createElement('iframe');
    frame.className = 'cp-reel__embed';
    frame.src = this.embed;
    frame.loading = 'lazy';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('title', this.root.dataset.cpEmbedTitle || '');
    this.root.appendChild(frame);

    this.started = true;
    this.root.classList.add('is-playing');
    pauseOthers(this);
    return true;
  };

  Player.prototype.start = function () {
    if (this.mountEmbed()) return;
    if (!this.video) return;

    /* The visitor pressed play, so they want sound. The element carries the
       `muted` attribute only so the browser never treats it as autoplayable. */
    this.video.muted = false;
    this.syncSoundLabel();

    var attempt = this.video.play();
    if (attempt && typeof attempt.catch === 'function') {
      var self = this;
      attempt.catch(function (error) {
        /* NotAllowedError means the autoplay policy blocked unmuted playback —
           Safari in particular rejects when it decides the gesture wasn't
           user-initiated. Falling back to muted beats leaving a dead poster.

           Every other rejection must be left alone. AbortError in particular
           means something deliberately interrupted us, which on this page is
           almost always the IntersectionObserver pausing a player that just
           scrolled away; retrying there would fight the pause and restart a
           video the visitor has already scrolled past. */
        if (error && error.name !== 'NotAllowedError') return;
        self.video.muted = true;
        self.syncSoundLabel();
        self.video.play().catch(function () {});
      });
    }
    this.started = true;
  };

  Player.prototype.pause = function () {
    if (this.video && !this.video.paused) this.video.pause();
  };

  Player.prototype.toggleSound = function () {
    if (!this.video) return;
    this.video.muted = !this.video.muted;
    /* Unmuting a hero that is sitting on its poster should also start it. */
    if (!this.video.muted && this.video.paused) this.start();
    this.syncSoundLabel();
  };

  Player.prototype.syncSoundLabel = function () {
    if (!this.soundBtn || !this.video) return;
    var muted = this.video.muted;
    var label = muted ? this.soundBtn.dataset.labelOn : this.soundBtn.dataset.labelOff;
    this.soundBtn.setAttribute('aria-label', label || '');
    this.soundBtn.setAttribute('aria-pressed', muted ? 'false' : 'true');
    var text = this.soundBtn.querySelector('[data-cp-sound-text]');
    if (text) text.textContent = muted ? this.soundBtn.dataset.textOn : this.soundBtn.dataset.textOff;
  };

  /* --- Visibility --------------------------------------------------------
     Pause-only. Scrolling a playing clip out of view stops it, so audio never
     follows the visitor down the page; scrolling back does NOT resume it,
     because nothing on this page starts without being asked to. */
  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) return;
          var player = entry.target.cpPlayer;
          if (player) player.pause();
        });
      },
      { threshold: 0.25 }
    );
  }

  function initMedia(scope) {
    scope.querySelectorAll('[data-cp-video]').forEach(function (root) {
      if (root.cpPlayer) return;

      var player = new Player(root);
      root.cpPlayer = player;
      players.push(player);

      if (observer) observer.observe(root);
    });
  }

  /* --- Embed thumbnails --------------------------------------------------
     Two jobs, both about not showing a broken or empty poster.

     `data-cp-thumb` holds YouTube's remaining thumbnail variants. The primary
     is already in `src`; each 404 steps to the next, and running out hides the
     image so the card falls back to the dark box and play glyph.

     `data-cp-oembed` is a TikTok/Vimeo oEmbed endpoint. Liquid cannot make
     that request, so it happens here — deferred until the card is near the
     viewport, because a reel strip can hold eight of them and none of it
     matters until the visitor scrolls sideways. */
  function initThumbs(scope) {
    scope.querySelectorAll('img[data-cp-thumb]').forEach(function (img) {
      if (img.dataset.cpThumbReady) return;
      img.dataset.cpThumbReady = 'true';

      var queue = img.dataset.cpThumb.split('|').filter(Boolean);
      var advance = function () {
        var next = queue.shift();
        if (next) img.src = next;
        else img.hidden = true;   // leaves the dark box and play glyph
      };

      img.addEventListener('error', advance);

      /* The load handler is the one that matters. YouTube answers a missing
         thumbnail variant with a 120x90 grey placeholder and a decodable
         body, so the image fires `load` rather than `error` — an onerror
         chain alone silently never advances. Size is the only reliable
         signal that a variant is genuinely absent; no real variant is that
         small (hqdefault, the smallest, is 480x360). */
      img.addEventListener('load', function () {
        if (img.naturalWidth <= 120 && img.naturalHeight <= 90) advance();
      });
    });

    scope.querySelectorAll('img[data-cp-oembed]').forEach(function (img) {
      if (img.dataset.cpOembedReady) return;
      img.dataset.cpOembedReady = 'true';

      var resolve = function () {
        fetch(img.dataset.cpOembed, { mode: 'cors', credentials: 'omit' })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (data) {
            if (!data || !data.thumbnail_url) return;
            /* A dead thumbnail URL should leave the placeholder, not a broken
               image icon. */
            img.addEventListener('error', function () { img.hidden = true; });
            img.src = data.thumbnail_url;
            img.hidden = false;
          })
          .catch(function () {
            /* Rate limited, offline, or the post was deleted. The card still
               works — it just keeps the placeholder. */
          });
      };

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              io.disconnect();
              resolve();
            });
          },
          { rootMargin: '200px' }
        );
        /* Watch the card, not the image. The image starts hidden so it has no
           box, and a display:none element never reports as intersecting —
           observing it directly means the lookup never fires. */
        io.observe(img.closest('.cp-media') || img.parentNode || img);
      } else {
        resolve();
      }
    });
  }

  /* --- FAQ ---------------------------------------------------------------
     Native <details> gives keyboard support and works with JS off; this only
     adds the single-open coordination. `open` is the source of truth, so
     aria-expanded is left to the element's own semantics. */
  function initFaq(scope) {
    scope.querySelectorAll('[data-cp-faq]').forEach(function (group) {
      if (group.dataset.cpFaqReady) return;
      group.dataset.cpFaqReady = 'true';

      var items = Array.prototype.slice.call(group.querySelectorAll('details'));
      items.forEach(function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          items.forEach(function (other) {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  function init(scope) {
    var root = scope || document;
    initMedia(root);
    initThumbs(root);
    initFaq(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor: a re-rendered section brings new DOM with no listeners. */
  document.addEventListener('shopify:section:load', function (event) {
    /* Drop players whose DOM was replaced, so pauseOthers() does not hold a
       growing list of detached nodes across editor edits. */
    players = players.filter(function (player) {
      return player.root.isConnected;
    });
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    players = players.filter(function (player) {
      return player.root.isConnected;
    });
  });
})();
